<template>
  <div>
    <!-- Loading -->
    <div v-if="loading" class="flex flex-col items-center justify-center py-12">
      <div
        class="h-8 w-8 animate-spin rounded-full border-2 border-slate-600 border-t-emerald-400"
      ></div>
      <p class="mt-4 text-sm text-slate-400">Loading schedule…</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="px-3 py-6 text-center">
      <span
        class="inline-block rounded-lg border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-300"
      >
        {{ error }}
      </span>
    </div>

    <!-- Both teams side by side, at every width. Comparing the two is the
         entire reason this panel exists, so the columns never stack.
         One hairline divider between them, not a box around each --
         GameCard is already the card; a per-team card inside it was the
         nesting this replaced. -->
    <div v-else>
      <div class="grid grid-cols-2 divide-x divide-white/10">
        <section
          v-for="column in columns"
          :key="column.team.abbr"
          class="min-w-0"
          :class="column === columns[0] ? 'pr-2 sm:pr-3' : 'pl-2 sm:pl-3'"
        >
          <header
            class="flex items-center gap-2 border-b-2 py-1.5"
            :style="{ borderColor: teamAccent(column.team) }"
          >
            <TeamLogoChip
              :team="column.team.name"
              :abbr="column.team.abbr"
              :logo="column.team.logo"
              size="sm"
            />
            <div class="min-w-0 flex-1">
              <p class="truncate text-sm font-bold leading-tight text-white">
                {{ column.team.abbr }}
              </p>
              <p class="truncate text-[10px] leading-tight text-slate-400">
                {{ column.label }}
              </p>
            </div>
          </header>

          <p v-if="!column.games.length" class="py-4 text-xs text-slate-500">
            No completed games yet.
          </p>
          <ul v-else class="divide-y divide-white/[0.06]">
            <PastGameRow
              v-for="gm in column.shown"
              :key="gm.id"
              :game="gm"
              :team="column.team"
            />
          </ul>
        </section>
      </div>

      <!-- Both columns expand together so the two records stay row-aligned. -->
      <button
        v-if="canExpand"
        type="button"
        class="mt-2 w-full rounded-lg border border-white/10 py-1.5 text-[11px] font-semibold text-emerald-400 hover:bg-white/[0.03] hover:text-emerald-300"
        @click="expanded = !expanded"
      >
        {{ expanded ? "Show fewer" : `Show all ${maxGames}` }}
      </button>
    </div>
  </div>
</template>

<script>
import api from "@/utils/api";
import PastGameRow from "./PastGameRow.vue";
import TeamLogoChip from "./TeamLogoChip.vue";
import { teamTint } from "../utils/teamColors.js";

export default {
  name: "PastGames",
  components: { PastGameRow, TeamLogoChip },
  props: {
    game: { type: Object, required: true },
    // Rows per team before "Show all"; unset shows every completed game.
    limit: { type: Number, default: null },
  },
  data() {
    return {
      loading: true,
      error: null,
      homeGames: [],
      awayGames: [],
      expanded: false,
    };
  },
  computed: {
    // Away first, matching the order the card above lists the two teams in.
    columns() {
      const cap = (games) =>
        this.limit != null && !this.expanded ? games.slice(0, this.limit) : games;

      return [
        { team: this.game.awayTeam, games: this.awayGames },
        { team: this.game.homeTeam, games: this.homeGames },
      ].map((column) => ({
        ...column,
        shown: cap(column.games),
        label: this.summarize({ ...column, games: cap(column.games) }),
      }));
    },
    maxGames() {
      return Math.max(this.homeGames.length, this.awayGames.length);
    },
    canExpand() {
      return this.limit != null && this.maxGames > this.limit;
    },
  },
  methods: {
    // Straight-up record over exactly the games listed below, so the header is
    // a summary of what you are looking at rather than a season number that
    // would not add up against it.
    summarize({ team, games }) {
      let wins = 0;
      let losses = 0;
      let ties = 0;

      games.forEach((gm) => {
        const home = this.isTeam(gm.homeTeam, team);
        const us = home ? gm.homeTeam : gm.awayTeam;
        const them = home ? gm.awayTeam : gm.homeTeam;
        if (Number(us.score) > Number(them.score)) wins += 1;
        else if (Number(us.score) < Number(them.score)) losses += 1;
        else ties += 1;
      });

      let record = `${wins}-${losses}`;
      if (ties) record += `-${ties}`;
      return `${record} · last ${games.length}`;
    },
    isTeam(side, team) {
      return (
        side.displayName === team.name ||
        side.name === team.name ||
        side.abbr === team.abbr
      );
    },
    teamAccent(team) {
      return teamTint(team.name || team.abbr, 0.55);
    },
    async fetchSchedule() {
      this.loading = true;
      this.error = null;

      try {
        const response = await api.get("/api/v1/schedules/", {
          params: {
            home_team_id: this.game.homeTeam.id,
            away_team_id: this.game.awayTeam.id,
            _t: Date.now(), // Cache busting
          },
          headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
          timeout: 30000,
        });

        this.homeGames = this.parseGames(response.data.home_schedule);
        this.awayGames = this.parseGames(response.data.away_schedule);
      } catch (error) {
        if (error.code === "ECONNABORTED") {
          this.error = "Request timed out. Please try again.";
        } else if (error.response) {
          this.error = `Server error: ${error.response.status}`;
        } else if (error.request) {
          this.error = "Network error. Please check your connection.";
        } else {
          this.error = "Failed to load schedule. Please try again later.";
        }
      } finally {
        this.loading = false;
      }
    },

    parseGames(events) {
      return (events || []).reduce((acc, event) => {
        const homeTeam = event.home_team;
        const awayTeam = event.away_team;

        if (homeTeam.score && awayTeam.score) {
          acc.push({
            id: event.id,
            gameId: event.game_id,
            date: new Date(event.date),
            week: event.week,
            homeTeam: {
              abbr: homeTeam.abbr,
              displayName: homeTeam.display_name,
              logo: homeTeam.logo,
              name: homeTeam.name,
              score: homeTeam.score,
              winner: homeTeam.winner,
            },
            awayTeam: {
              abbr: awayTeam.abbr,
              displayName: awayTeam.display_name,
              logo: awayTeam.logo,
              name: awayTeam.name,
              score: awayTeam.score,
              winner: awayTeam.winner,
            },
          });
        }

        return acc;
      }, []);
    },
  },

  async mounted() {
    await this.fetchSchedule();
  },
};
</script>
